import { useRef } from 'react';

import clsx from 'clsx';
import dynamic from 'next/dynamic';

import { GETAPP_ORGANIZATION_TYPE_OPTIONS } from 'bx-forms/eas/getapp';
import Accordion from 'components/Accordion';
import BackToTop from 'components/BackToTop';
import { BuyerIntentLeadClick } from 'components/BuyerIntent';
import Category from 'components/Category';
import CtaButton from 'components/CtaButton';

const InlineForm = dynamic(
  () => import('bx-forms/eas/getapp').then((mod) => mod.InlineForm),
  { ssr: false }
);
import Grid, { Container } from 'components/Grid';
import ImpressionEvent from 'components/ImpressionEvent';
import OneTapScript from 'components/OneTapScript';
import Paper from 'components/Paper';
import ProductInfoCard from 'components/ProductInfoCard';
import SoftwareApplicationLD from 'components/SoftwareApplicationLD/SoftwareApplicationLD';
import SplashElement from 'components/SplashElement';
import Typography from 'components/Typography';
import { useAppContext } from 'context/AppContext';
import { EventContext, useEventContext } from 'context/EventContext';
import { ListingDetailProvider } from 'context/ListingDetailContext';
import { useUser } from 'hooks';
import usePplForm from 'hooks/usePplForm';
import useViewport from 'hooks/useViewport';

import PPLPricingBanner from 'components/PPLPricingBanner';
import PPLSimpleBanner from 'components/PPLSimpleBanner';
import UserOpinions from '../../pricing/components/UserOpinions';
import Faq from '../components/Faq';
import ManageProductListing from '../components/ManageProductListing';
import PopularComparisons from '../components/PopularComparisons';
import TableOfContent from '../components/TableOfContent';
import PPLBanner from '../components/TableOfContent/components/PPLBanner';
import PPLBannerVariant from '../components/TableOfContent/components/PPLBannerVariant';
import Alternatives from './components/Alternatives';
import Authors from './components/Authors';
import CategoryLeaders from './components/CategoryLeaders';
import CustomerSupportSummary from './components/CustomerSupportSummary';
import FeaturesOverview from './components/FeaturesOverview';
import Integrations from './components/Integrations';
import KeyFeatures from './components/KeyFeatures';
import KeyTakeaways from './components/KeyTakeaways';
import PricingOverview from './components/Pricing';
import PricingPlans from './components/PricingPlans';
import ProConsAdvisorBanner from './components/ProConsAdvisorBanner';
import Reviews from './components/Reviews';
import ReviewsSummary from './components/ReviewsSummary';

import UserInteface from './components/UserInteface';
import UsersAndUseCases from './components/UsersAndUseCases';
import ValueForMoneySummaries from './components/ValueForMoneySummaries';

import classes from './ListingDetailV1.module.scss';

const ListingDetailV1 = ({
  listing,
  showPricingPlans,
  showPplPricingBanner,
  charts,
}) => {
  const { isCypressTakingSnapshots, head, header } = useAppContext();
  const { up, is } = useViewport();
  const isMobile = is('xs');
  const isDesktop = up('md');
  const currentYear = new Date().getFullYear();
  const { sendEvent, addEvent } = useEventContext();
  const stickyContainerRef = useRef();

  const showPplCtas = showPplPricingBanner && !listing.is_premium;

  const {
    buyerIntentProductId,
    buyerIntentCategoryId,
    handleClick: handleDemoClick,
    openForm: openDemoForm,
  } = usePplForm({
    productId: listing.global_product_id,
    sourceLocation: 'listing-detail-v1/user-interface:see-in-action',
    formTemplate: 'demo',
    includeProductInConfig: true,
    enabled: showPplCtas,
  });
  const { handleClick: handleFindPerfectMatchClick } = usePplForm({
    productId: listing.global_product_id,
    sourceLocation: 'listing-detail-v1/alternatives:find-perfect-match',
    formTemplate: 'faststart',
    enabled: showPplCtas,
  });
  const { handleClick: handleFindRightFitClick } = usePplForm({
    productId: listing.global_product_id,
    sourceLocation: 'listing-detail-v1/features:find-right-fit',
    formTemplate: 'faststart',
    enabled: showPplCtas,
  });
  const [user] = useUser();

  const { leaders } = listing.categories.reduce(
    (acc, category) => ({
      leaders: category.category_leaders
        ? acc.leaders.concat([category])
        : acc.leaders,
    }),
    { leaders: [] }
  );

  const hasPricing =
    listing.pricing.amount !== null || listing.pricing.categories.length !== 0;
  const hasCategoryLeaders = !!leaders.length;
  const hasAlternatives = !!listing.alternatives_comparisons.length;
  const hasKeyFeatures = !!listing.features?.enhanced_features.length;
  const hasCustommerSupport =
    !!listing.typical_customers.length ||
    !!listing.platforms.length ||
    !!listing.support_options.length ||
    !!listing.training_options.length;
  const hasFAQs = !!listing.faq.length;
  const hasPopularComparisons = !!listing.popular_comparisons.length;
  const hasRelatedCategories = !!listing.categories.length;
  const hasIntegrations =
    Array.isArray(listing.integrations) && listing.integrations.length > 0;
  const hasPPLDemoForm = head?.hasPplDemoFormEnabled;
  const hasScreenshots = !!listing.screenshots.length;
  const hasKeyTakeaways = !!listing.key_takeaways_summary;
  const hasCustomerSupportSummary = !!listing.customer_support_summary;
  const hasReviewsSummary = !!listing.reviews_summaries_v2;

  const hasUsersAndUseCases =
    Object.keys(charts).length > 0 &&
    ((charts.company_sizes && Object.keys(charts.company_sizes).length > 0) ||
      (charts.top_industries &&
        Object.keys(charts.top_industries).length > 0) ||
      (charts.use_cases && Object.keys(charts.use_cases).length > 0));

  const SECTIONS = [
    {
      menuTitle: `${listing.name} overview`,
      id: hasKeyTakeaways ? 'key-takeaways' : 'overview',
    },
    hasScreenshots ? { menuTitle: 'User interface', id: 'user-interface' } : {},
    { menuTitle: `Reviews`, id: 'reviews' },
    hasUsersAndUseCases
      ? { menuTitle: `Who uses ${listing.name}?`, id: 'users-and-use-cases' }
      : {},
    {
      menuTitle: hasKeyFeatures ? 'Key features' : 'Features',
      id: 'key-features',
    },
    hasCategoryLeaders ? { menuTitle: 'Awards', id: 'category-leaders' } : {},
    hasAlternatives
      ? { menuTitle: 'Alternatives', id: 'alternatives-comparison' }
      : {},
    hasPricing ? { menuTitle: 'Pricing', id: 'pricing' } : {},
    hasIntegrations ? { menuTitle: 'Integrations', id: 'integrations' } : {},
    hasCustommerSupport
      ? { menuTitle: 'Support options', id: 'support-options' }
      : {},
    hasFAQs ? { menuTitle: 'FAQs', id: 'faqs' } : {},
    hasPopularComparisons
      ? { menuTitle: 'Popular comparisons', id: 'popular-comparisons' }
      : {},
    hasRelatedCategories
      ? { menuTitle: 'Related categories', id: 'categories' }
      : {},
  ].filter(({ id }) => id);

  const handleTableOfContentExpand = (_, { isExpanded }) => {
    sendEvent(
      isExpanded
        ? {
            name: 'engagement_navigation',
            engagementComponent: 'table-of-contents',
            engagementDestination: 'open_table-of-content',
            engagementDetails: 'expand_table-of-content',
          }
        : {
            name: 'engagement_navigation',
            engagementComponent: 'table-of-contents',
            engagementDetails: 'close_table-of-content',
            engagementDestination: 'collapse_table-of-content',
          }
    );
  };

  return (
    <ListingDetailProvider data={{ listing }}>
      <ProductInfoCard
        isCypressTakingSnapshots={isCypressTakingSnapshots}
        listingInfoData={listing}
        head={head}
        header={header}
        hideLastUpdated={true}
        buyerIntentMainCtaSourceLocation="listing/product-info-card:main-cta"
      />
      <SplashElement />
      <SoftwareApplicationLD listing={listing} />
      <OneTapScript user={user} />
      <div>
        <Container fixedWidth={true} className={classes.pageContentContainer}>
          <Grid container={true} justifyContent="center" spacing={3}>
            {!isDesktop && (
              <>
                <Grid item={true} xs={12}>
                  <Typography
                    component="h1"
                    variant="heading-lg"
                    className={classes.heading}
                  >
                    {`${listing.name} - ${currentYear} Pricing, Features, Reviews & Alternatives`}
                  </Typography>
                </Grid>
                <Authors lastUpdatedDate={listing.last_updated_on} />
              </>
            )}
            {isDesktop ? (
              <Grid item={true} md={3} className={classes.stickyContainer}>
                <div ref={stickyContainerRef}>
                  {/* Sticky filters alters the DOM and react can't find the correct parent, adding a div to prevent this
                    issue */}
                  <div>
                    <TableOfContent
                      content={SECTIONS}
                      stickyContainerRef={stickyContainerRef}
                      showPplPricingBanner={showPplPricingBanner}
                    />
                  </div>
                </div>
              </Grid>
            ) : (
              <Grid item={true} xs={12}>
                <Paper>
                  <Accordion
                    onExpandClick={handleTableOfContentExpand}
                    summary={
                      <Typography variant="heading-sm">
                        Table of contents
                      </Typography>
                    }
                  >
                    <TableOfContent
                      content={SECTIONS}
                      showPplPricingBanner={showPplPricingBanner}
                    />
                  </Accordion>
                </Paper>
              </Grid>
            )}
            <Grid item={true} xs={12} md={9}>
              {isDesktop && (
                <>
                  <Grid item={true} xs={12}>
                    <Typography
                      component="h1"
                      variant="heading-lg"
                      className={classes.heading}
                    >
                      {`${listing.name} - ${currentYear} Pricing, Features, Reviews & Alternatives`}
                    </Typography>
                  </Grid>
                  <Authors lastUpdatedDate={listing.last_updated_on} />
                </>
              )}
              <section id="key-takeaways" className="ContentItem">
                <KeyTakeaways
                  listing={listing}
                  showPplPricingBanner={showPplPricingBanner}
                />
                <ManageProductListing
                  productName={listing.name}
                  g2ProductSlug={listing.g2ProductSlug}
                  engagementComponent="key-takeaways"
                />
              </section>

              {hasScreenshots && (
                <section id="user-interface" className="ContentItem">
                  <div className={classes.titleContainer}>
                    <Typography component="h2" className={classes.title}>
                      {listing.name}’s user interface
                    </Typography>
                  </div>
                  <UserInteface
                    ratingAvg={listing.rating_avg}
                    numReviews={listing.num_reviews}
                  />
                  {showPplCtas && (
                    <BuyerIntentLeadClick
                      productId={buyerIntentProductId}
                      categoryId={buyerIntentCategoryId}
                      sourceLocation="listing-detail-v1/user-interface:see-in-action"
                    >
                      <div className={classes.pplDemoCta}>
                        <CtaButton
                          onClick={handleDemoClick}
                          {...addEvent({
                            engagementComponent: 'ppl_banner',
                            engagementDestination: 'go-to_ppl-demo-form',
                            engagementDetails: 'button_see-in-action',
                            name: 'ppl_cta',
                          })}
                        >
                          See this product in action
                        </CtaButton>
                      </div>
                    </BuyerIntentLeadClick>
                  )}
                </section>
              )}

              {hasReviewsSummary ? (
                <section id="reviews" className="ContentItem">
                  <div className={classes.titleContainer}>
                    <Typography component="h2" className={classes.title}>
                      {`${listing.name} review summaries overview`}
                    </Typography>
                  </div>
                  <ReviewsSummary listing={listing} />
                  {showPplCtas && <ProConsAdvisorBanner listing={listing} />}
                </section>
              ) : (
                <section id="reviews" className="ContentItem">
                  <div className={classes.titleContainer}>
                    <Typography component="h2" className={classes.title}>
                      {`${listing.name} reviews`}
                    </Typography>
                  </div>
                  <Reviews listing={listing} />
                  {showPplCtas && <ProConsAdvisorBanner listing={listing} />}
                </section>
              )}

              {hasUsersAndUseCases && (
                <section id="users-and-use-cases" className="ContentItem">
                  <UsersAndUseCases
                    charts={charts}
                    totalReviews={listing.reviews.total}
                    listingName={listing.name}
                  />
                </section>
              )}

              <section id="key-features" className="ContentItem">
                <div className={classes.titleContainer}>
                  {hasKeyFeatures && (
                    <Typography component="h2" className={classes.title}>
                      {listing.name}&apos;s key features
                    </Typography>
                  )}
                </div>
                <KeyFeatures />
                {showPplCtas && (
                  <div className={classes.faststartCtaContainer}>
                    <CtaButton
                      onClick={handleFindRightFitClick}
                      {...addEvent({
                        engagementComponent: 'ppl_banner',
                        engagementDestination: 'go-to_ppl-faststart-form',
                        engagementDetails: 'features_find-right-fit',
                        name: 'ppl_cta',
                      })}
                    >
                      FIND THE RIGHT FIT
                    </CtaButton>
                  </div>
                )}
              </section>
              {hasPPLDemoForm && !isDesktop && (
                <ImpressionEvent
                  event={{
                    engagementComponent: 'ppl_banner',
                    engagementDestination: 'banner_displayed',
                    engagementDetails: 'scrolled-to_ppl_banner',
                    name: 'section_impression',
                  }}
                >
                  {showPplPricingBanner ? (
                    <PPLBannerVariant show={showPplCtas} />
                  ) : (
                    <PPLBanner />
                  )}
                </ImpressionEvent>
              )}
              {hasCategoryLeaders && (
                <section id="category-leaders" className={'ContentItem'}>
                  <div className={classes.titleContainer}>
                    <Typography component="h2" className={classes.title}>
                      {`${listing.name} awards`}
                    </Typography>
                  </div>
                  <CategoryLeaders leaders={leaders} />
                </section>
              )}
              {hasAlternatives && (
                <section
                  id="alternatives-comparison"
                  className={clsx('ContentItem', classes.alternativesContainer)}
                >
                  <div className={classes.titleContainer}>
                    <Typography component="h2" className={classes.title}>
                      {`${listing.name} alternatives`}
                    </Typography>
                  </div>
                  <Alternatives
                    listing={listing}
                    showPplPricingBanner={showPplPricingBanner}
                  />
                  {showPplCtas && (
                    <BuyerIntentLeadClick
                      productId={buyerIntentProductId}
                      categoryId={buyerIntentCategoryId}
                      sourceLocation="listing-detail-v1/alternatives:find-perfect-match"
                    >
                      <div className={classes.pplDemoCta}>
                        <CtaButton
                          onClick={handleFindPerfectMatchClick}
                          {...addEvent({
                            engagementComponent: 'ppl_banner',
                            engagementDestination: 'go-to_ppl-faststart-form',
                            engagementDetails: 'button_find-perfect-match',
                            name: 'ppl_cta',
                          })}
                        >
                          Find your perfect match
                        </CtaButton>
                      </div>
                    </BuyerIntentLeadClick>
                  )}
                </section>
              )}

              {!isMobile && !isCypressTakingSnapshots && (
                <Grid
                  item={true}
                  xs={12}
                  className={classes.serviceFormContainer}
                >
                  <InlineForm
                    formId="7yeqBVioyHygFTd2tGSavD"
                    gatewayUrl="https://www.capterra.com/bx-capterra-api/bx-forms/submit"
                    formTemplate="INLINE_CARD"
                    qualificationOptions={GETAPP_ORGANIZATION_TYPE_OPTIONS}
                    step2QuestionLabel="Which best describes your organization type?"
                    headline="Send this software info to my inbox"
                    subHeading=""
                    buttonText="Send me the info"
                    successMessage="Your request will be delivered to your inbox shortly. If you don't see an email from GetApp in the next 5 minutes, check your spam folder."
                    step2Subtitle="You're in! To receive personalized recommendations, share your organization type:"
                    contextProps={{
                      pageType: 'spotlight',
                      pagePlacement: 'inline',
                    }}
                    tracking={{
                      formName: 'DMBUY_2022_GA_Blog_Acq',
                      contentCategory: 'Spotlight_Desktop_Inline',
                      contentRequest: 'Product info',
                    }}
                  />
                </Grid>
              )}
              {isMobile && !isCypressTakingSnapshots && (
                <Grid
                  item={true}
                  xs={12}
                  className={classes.serviceFormContainer}
                >
                  <InlineForm
                    formId="8uFCV5LPWB334oMjiKB1A"
                    gatewayUrl="https://www.capterra.com/bx-capterra-api/bx-forms/submit"
                    formTemplate="INLINE_CARD"
                    qualificationOptions={GETAPP_ORGANIZATION_TYPE_OPTIONS}
                    step2QuestionLabel="Which best describes your organization type?"
                    headline="Send this software info to my inbox"
                    subHeading=""
                    buttonText="Send me the info"
                    successMessage="Your request will be delivered to your inbox shortly. If you don't see an email from GetApp in the next 5 minutes, check your spam folder."
                    step2Subtitle="You're in! To receive personalized recommendations, share your organization type:"
                    contextProps={{
                      pageType: 'spotlight',
                      pagePlacement: 'inline',
                      pageVariant: 'mobile',
                    }}
                    tracking={{
                      formName: 'DMBUY_2022_GA_Blog_Acq',
                      contentCategory: 'Spotlight_Mobile_Inline',
                      contentRequest: 'Product info',
                    }}
                  />
                </Grid>
              )}

              {hasPricing && (
                <section id="pricing" className="ContentItem">
                  <div className={classes.titleContainer}>
                    <Typography component="h2" className={classes.title}>
                      {`${listing.name} pricing`}
                    </Typography>
                  </div>

                  {showPricingPlans && listing.pricing?.plans?.length > 1 ? (
                    <PricingPlans
                      listing={listing}
                      showPplPricingBanner={showPplPricingBanner}
                    />
                  ) : (
                    <PricingOverview
                      listing={listing}
                      showValueForMoney={
                        !listing?.value_for_money_summary?.summary
                      }
                      showPplPricingBanner={showPplPricingBanner}
                    />
                  )}

                  {listing?.value_for_money_summary?.summary ? (
                    <ValueForMoneySummaries
                      listing={listing}
                      className={classes.aiSummaries}
                    />
                  ) : (
                    <UserOpinions
                      reviews={listing.reviews}
                      productName={listing.name}
                    />
                  )}
                </section>
              )}
              {hasIntegrations && (
                <section
                  id="integrations"
                  className={clsx('ContentItem', {
                    [classes.noPaddingBottom]: showPplCtas,
                  })}
                >
                  <Integrations listing={listing} />
                  {showPplCtas && (
                    <PPLSimpleBanner
                      listing={listing}
                      boldText="Check compatibility with your tools."
                      description=" Make sure your next software works perfectly with the stack your team relies on."
                      sourceLocation="listing-detail-v1/integrations:get-free-advice"
                      engagementDetails="integrations_get-free-advice"
                    />
                  )}
                </section>
              )}

              {hasCustomerSupportSummary && (
                <section id="support-options" className="ContentItem">
                  <CustomerSupportSummary listing={listing} />
                </section>
              )}

              {!hasCustomerSupportSummary && hasCustommerSupport && (
                <section id="support-options" className="ContentItem">
                  <div className={classes.titleContainer}>
                    <Typography component="h2" className={classes.title}>
                      {`${listing.name} support options`}
                    </Typography>
                  </div>
                  <FeaturesOverview listing={listing} />
                </section>
              )}

              <section id="faqs" className="ContentItem">
                <div className={classes.titleContainer}>
                  <Typography component="h2" className={classes.title}>
                    {`${listing.name} FAQs`}
                  </Typography>
                </div>
                <Faq listing={listing} />
              </section>
              {hasPopularComparisons && (
                <section
                  id="popular-comparisons"
                  className={clsx('ContentItem', {
                    [classes.noPaddingBottom]: showPplCtas,
                  })}
                >
                  <div className={classes.titleContainer}>
                    <Typography component="h2" className={classes.title}>
                      {`Popular comparisons with ${listing.name}`}
                    </Typography>
                  </div>
                  <PopularComparisons listing={listing} />
                  <PPLPricingBanner
                    show={showPplCtas}
                    title="Stuck Between Options?"
                    description={`Our experts can help you compare ${listing.name} with other top options, so you can find the best fit for your needs.`}
                    ctaLabel="Get Advice Now"
                    sourceLocation="listing-detail-v1/popular-comparisons:get-advice"
                    engagementDetails="popular-comparisons_get-advice"
                    formTemplate="faststart"
                  />
                </section>
              )}
              {hasRelatedCategories && (
                <section id="categories" className="ContentItem">
                  <div className={classes.titleContainer}>
                    <Typography component="h2" className={classes.title}>
                      Related categories
                    </Typography>
                  </div>
                  <Grid container={true}>
                    <Grid item={true} xs={12}>
                      <Grid container={true} spacing={2}>
                        <EventContext
                          value={{
                            engagementComponent: 'product_related-categories',
                          }}
                        >
                          {listing.categories.map((category) => (
                            <Grid
                              item={true}
                              key={category.id}
                              xs={12}
                              sm={6}
                              md={4}
                            >
                              <Category.MiniCard category={category} />
                            </Grid>
                          ))}
                        </EventContext>
                      </Grid>
                    </Grid>
                  </Grid>
                </section>
              )}
            </Grid>
          </Grid>
        </Container>
        {!isDesktop && <BackToTop className={classes.backToTop} />}
      </div>
    </ListingDetailProvider>
  );
};

export default ListingDetailV1;
